import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Badge, Button, Select, Skeleton, Switch } from '@indipia/ui-core'
import {
  getTodayTodoNotiSetting,
  patchTodayTodoNotiSetting,
  resetTodayTodoNotiSetting,
  type TodayTodoNotiPatch,
  type TodayTodoNotiSetting,
} from '../api/foremanApi'

// 공사알림 설정 — 오늘 할일 알림 시각 행 (기획서 H-3 studio-sched-notify-web).
// 변경 즉시 PATCH · 낙관적 갱신 · 실패 시 마지막 서버 확정값으로 복귀.
// 연속 변경은 변경 필드를 모아(마지막 값 우선) 한 번에 하나씩만 전송한다.
// 변경·초기화는 OWNER 만 가능(STAFF 는 403) — 안내 문구로 알린다.

// 알림톡 금지 시간대(22:00~익일 06:00, 06:00 정각은 허용)를 뺀 06:00~21:50 만 선택 가능 — 시 · 분(10분 단위) 두 Select.
// 서버 검증(TodayTodoNotiSettingService)과 같은 범위.
const HOUR_MIN = 6
const HOUR_MAX = 21
const MINUTE_STEP = 10
const pad2 = (n: number) => String(n).padStart(2, '0')
const hourLabel = (h: number) => `${pad2(h)}시`
const minuteLabel = (m: number) => `${pad2(m)}분`
const HOUR_OPTIONS = Array.from({ length: HOUR_MAX - HOUR_MIN + 1 }, (_, i) => HOUR_MIN + i).map(h => ({ value: String(h), label: hourLabel(h) }))
const MINUTE_OPTIONS = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP).map(m => ({ value: String(m), label: minuteLabel(m) }))
const isValidMinute = (m: number) => m >= 0 && m < 60 && m % MINUTE_STEP === 0

const labelStyle = { fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' } as const
const descStyle = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginTop: 2 } as const

function isForbidden(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 403
}

export function TodayTodoNotiSettingRow() {
  const [setting, setSetting] = useState<TodayTodoNotiSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [busy, setBusy] = useState(false)

  const confirmedRef = useRef<TodayTodoNotiSetting | null>(null)
  const pendingRef = useRef<TodayTodoNotiPatch>({})
  const inflightRef = useRef(false)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    getTodayTodoNotiSetting()
      .then(s => { confirmedRef.current = s; setSetting(s) })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [reloadKey])

  const flush = useCallback(async () => {
    if (inflightRef.current) return
    inflightRef.current = true
    setBusy(true)
    try {
      while (Object.keys(pendingRef.current).length > 0) {
        const patch = pendingRef.current
        pendingRef.current = {}
        try {
          const res = await patchTodayTodoNotiSetting(patch)
          confirmedRef.current = { ...(confirmedRef.current as TodayTodoNotiSetting), ...patch, isDefault: res?.isDefault ?? false }
        } catch (err) {
          pendingRef.current = {}
          if (aliveRef.current) setSetting(confirmedRef.current)
          toast.error(isForbidden(err) ? '사장님(대표 계정)만 바꿀 수 있어요' : '저장하지 못했어요. 다시 시도해 주세요')
        }
      }
    } finally {
      inflightRef.current = false
      if (aliveRef.current) setBusy(false)
    }
  }, [])

  const change = (patch: TodayTodoNotiPatch) => {
    setSetting(prev => (prev ? { ...prev, ...patch } : prev))
    pendingRef.current = { ...pendingRef.current, ...patch }
    void flush()
  }

  const handleReset = async () => {
    if (inflightRef.current) return
    inflightRef.current = true
    setBusy(true)
    pendingRef.current = {}
    try {
      const res = await resetTodayTodoNotiSetting()
      confirmedRef.current = res
      setSetting(res)
      toast.success('기본값(켜짐 · 07:00)으로 되돌렸어요')
    } catch (err) {
      toast.error(isForbidden(err) ? '사장님(대표 계정)만 바꿀 수 있어요' : '초기화하지 못했어요. 다시 시도해 주세요')
    } finally {
      inflightRef.current = false
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'nowrap', padding: '15px 18px', borderTop: '1px solid var(--shell-border)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
          오늘 할일 알림 시각
          <Badge variant="soft" status="info">신규</Badge>
        </div>
        <div style={descStyle}>착공 전 · 진행중 현장에 대해 오늘 체크해야 할 사항을 알려드려요</div>
      </div>
      {loading ? (
        <Skeleton height={36} width={200} />
      ) : loadError || !setting ? (
        <Button variant="etc" size="sm" onClick={() => setReloadKey(k => k + 1)}>다시 불러오기</Button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {setting.enabled ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Select
                aria-label="오늘 할일 알림 시"
                size="sm"
                value={String(setting.hour)}
                onChange={e => change({ hour: Number(e.target.value) })}
              >
                {(setting.hour < HOUR_MIN || setting.hour > HOUR_MAX) && (
                  // 과거에 저장된 금지 시각 — 현재 값으로는 표시하되 다시 고를 수 없게 비활성
                  <option value={String(setting.hour)} disabled>{hourLabel(setting.hour)}</option>
                )}
                {HOUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
              <Select
                aria-label="오늘 할일 알림 분"
                size="sm"
                value={String(setting.minute)}
                onChange={e => change({ minute: Number(e.target.value) })}
              >
                {!isValidMinute(setting.minute) && (
                  <option value={String(setting.minute)} disabled>{minuteLabel(setting.minute)}</option>
                )}
                {MINUTE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </div>
          ) : (
            <span style={{ ...descStyle, marginTop: 0 }}>알림을 보내지 않아요</span>
          )}
          <Switch
            aria-label="오늘 할일 알림"
            checked={setting.enabled}
            onChange={e => change({ enabled: e.target.checked })}
          />
          <Button variant="etc" size="sm" onClick={() => { void handleReset() }} disabled={busy || setting.isDefault}>초기화</Button>
        </div>
      )}
    </div>
  )
}
